<template>
	<input
		class="placeholder-label-secondary text-label-primary text-base focus:outline-none focus-visible:outline-none"
		:id="id"
		:type="type"
		:placeholder="placeholder"
		:value="props.modelValue"
		@input="input"
		:step="step"
		:min="min"
		:max="max"
		:inputmode="forceInputmode"
		spellcheck="false"
		ref="inputRef"
	/>
</template>
<script
	setup
	lang="ts"
	generic="
		InputType extends 'email' | 'text' | 'number' | 'password' | 'date',
		ValueType extends InputType extends 'number' ? number | null : string
	"
>
import { computed, ref } from 'vue'
import type { Component } from 'vue'

const props = withDefaults(
	defineProps<{
		id: string
		type: InputType
		modelValue?: ValueType
		placeholder?: string
		step?: number
		min?: number
		max?: number
		inputmode?:
			| 'text'
			| 'none'
			| 'tel'
			| 'url'
			| 'email'
			| 'numeric'
			| 'decimal'
			| 'search'
		component?: Component | 'input'
	}>(),
	{
		inputmode: 'text',
		component: 'input'
	}
)

const emit = defineEmits<{
	(e: 'update:modelValue', value: ValueType): void
}>()

const input = (e: Event) => {
	let value: ValueType

	if (props.type === 'number') {
		value = Number(
			(e.target as HTMLInputElement).value.replace('\,', '\.')
		) as ValueType

		if (isNaN(value as number) && (e.target as HTMLInputElement).value) {
			value = 0 as ValueType
		}

		if (typeof props.min !== 'undefined' && (value as number) < props.min) {
			value = props.min as ValueType
		}
		if (typeof props.max !== 'undefined' && (value as number) > props.max) {
			value = props.max as ValueType
		}

		if (!(e.target as HTMLInputElement).value) {
			value = null as ValueType
		}
	} else {
		value = (e.target as HTMLInputElement).value as ValueType
	}

	emit('update:modelValue', value)
}

const type = computed(() => (props.type === 'number' ? 'text' : props.type))
const forceInputmode = computed(() =>
	props.type !== 'number'
		? props.inputmode
		: props.step && props.step % 1 !== 0
			? 'decimal'
			: 'numeric'
)

const inputRef = ref<HTMLInputElement>()

const getInputElement = () => {
	if (!inputRef.value) {
		return
	}

	return inputRef.value
}

const focus = () => {
	const el = getInputElement()

	if (!el) {
		return
	}

	el.focus()
}

const select = () => {
	const el = getInputElement()

	if (!el) {
		return
	}

	el.select()
}

defineExpose({
	focus,
	select
})
</script>
